// A WebSocketAdapter over the browser's WebSocket, for @esengine/rpc's RpcClient on real network rooms.
// Three things the plain WebSocket gets wrong for RpcClient: it delivers binary frames as Blobs unless told otherwise
// (RpcClient reads them with `new Uint8Array(data)`, which yields an empty array for a Blob); RpcClient only hands the
// close reason to its callers, never the close code the room chose (CloseCode); and RpcClient reports the socket's own
// error events through onError as a plain Error, indistinguishable from a decode failure or a listener exception —
// so the socket marks them (SocketErrorScope), and an owner reports only the errors that are bugs.

import type { WebSocketAdapter } from '@esengine/rpc'

export interface SocketClose {
  readonly code: number
  readonly reason: string
}

/** Active while a socket forwards its own error event to RpcClient: an onError call then is that event, not a bug. */
export class SocketErrorScope {
  private depth = 0

  get active(): boolean {
    return this.depth > 0
  }

  run(forward: () => void): void {
    this.depth++
    try {
      forward()
    } finally {
      this.depth--
    }
  }
}

/** What a socket tells its owner beyond RpcClient's callbacks. */
export interface SocketObserver {
  /** How the socket closed, with the room's close code. */
  closed(close: SocketClose): void
  /** A transport failure (an open that failed, a broken link) is the close's to handle, never an error report. */
  readonly errors: SocketErrorScope
}

export class BrowserSocket implements WebSocketAdapter {
  onopen: ((ev: Event) => void) | null = null
  onclose: ((ev: { code: number; reason: string }) => void) | null = null
  onerror: ((ev: Event) => void) | null = null
  onmessage: ((ev: { data: string | ArrayBuffer }) => void) | null = null

  private readonly socket: WebSocket

  constructor(url: string, observer: SocketObserver) {
    this.socket = new WebSocket(url)
    this.socket.binaryType = 'arraybuffer'
    this.socket.addEventListener('open', (event) => this.onopen?.(event))
    this.socket.addEventListener('error', (event) => observer.errors.run(() => this.onerror?.(event)))
    this.socket.addEventListener('message', (event: MessageEvent<string | ArrayBuffer>) => this.onmessage?.({ data: event.data }))
    this.socket.addEventListener('close', (event) => {
      observer.closed({ code: event.code, reason: event.reason })
      this.onclose?.({ code: event.code, reason: event.reason })
    })
  }

  get readyState(): number {
    return this.socket.readyState
  }

  /** RpcClient passes the codec's Uint8Array (typed as ArrayBuffer); WebSocket.send copies a view's bytes at once. */
  send(data: string | ArrayBuffer): void {
    this.socket.send(data)
  }

  close(code?: number, reason?: string): void {
    this.socket.close(code, reason)
  }
}
